/* ============================================================================
   MEMORA DESKTOP FILE DROP
   Captures files dragged from the desktop into the active Memora tab. The
   feature is intentionally inert on phones and tablets/iPad-like devices.
   ============================================================================ */

// Pictures are study sources too: the import turns each into a one-page PDF.
// Word files become a quiz or a text PDF (app.docx-import.js); a legacy .doc gets its notice there.
const MEMORA_DESKTOP_FILE_DROP_STUDY_EXTENSIONS = Object.freeze([
    'pdf',
    'pptx',
    'docx',
    'doc',
    'json',
    'txt',
    'png',
    'jpg',
    'jpeg',
    'webp',
    'gif',
    'bmp'
]);
const MEMORA_DESKTOP_FILE_DROP_SPECIAL_TARGET_SELECTOR = [
    '#import-drop-zone',
    '#calendar-import-dropzone',
    '#calendar-import-ics-dropzone',
    '.memora-inline-cal-drop',
    '.pdf-study-guide-image-card'
].join(', ');

let memoraDesktopFileDropDragDepth = 0;
let memoraDesktopFileDropOverlay = null;
let memoraDesktopFileDropBound = false;

function getMemoraDesktopFileDropExtension(file) {
    const name = String(file?.name || '').trim().toLowerCase();
    const match = name.match(/\.([a-z0-9]+)$/);
    return match ? match[1] : '';
}

function classifyMemoraDesktopFileDrop(file) {
    const extension = getMemoraDesktopFileDropExtension(file);
    const mimeType = String(file?.type || '').trim().toLowerCase();

    if (MEMORA_DESKTOP_FILE_DROP_STUDY_EXTENSIONS.includes(extension)) return 'study';
    if (extension === 'ics' || mimeType === 'text/calendar') return 'calendar';
    if (extension === 'ppt') return 'legacy-presentation';
    if (mimeType.startsWith('image/')) return 'image';
    return 'unsupported';
}

function partitionMemoraDesktopFileDropFiles(files) {
    const groups = {
        study: [],
        calendar: [],
        image: [],
        legacyPresentation: [],
        unsupported: []
    };

    Array.from(files || []).forEach((file) => {
        const kind = classifyMemoraDesktopFileDrop(file);
        if (kind === 'legacy-presentation') groups.legacyPresentation.push(file);
        else if (groups[kind]) groups[kind].push(file);
        else groups.unsupported.push(file);
    });

    return groups;
}

function isMemoraDesktopFileDropDevice(deviceSupport = {}) {
    return deviceSupport?.isTabletLike !== true
        && deviceSupport?.isPhoneLike !== true;
}

function isMemoraDesktopFileDropEligible() {
    return isMemoraDesktopFileDropDevice(window.__MEMORA_DEVICE_SUPPORT__ || {});
}

function hasMemoraDesktopFileTransfer(event) {
    const types = Array.from(event?.dataTransfer?.types || [])
        .map((type) => String(type || '').toLowerCase());
    return types.includes('files') || Number(event?.dataTransfer?.files?.length || 0) > 0;
}

function getMemoraDesktopDroppedFiles(event) {
    return Array.from(event?.dataTransfer?.files || [])
        .filter((file) => file && typeof file.name === 'string');
}

function isMemoraDesktopFileDropSpecialTarget(target) {
    const element = target?.closest
        ? target
        : target?.parentElement;
    return !!element?.closest?.(MEMORA_DESKTOP_FILE_DROP_SPECIAL_TARGET_SELECTOR);
}

function ensureMemoraDesktopFileDropOverlay() {
    if (memoraDesktopFileDropOverlay?.isConnected) return memoraDesktopFileDropOverlay;
    if (!document.body) return null;

    const overlay = document.createElement('div');
    overlay.id = 'memora-desktop-file-drop-overlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = `
        <div class="memora-desktop-file-drop-card">
            <div class="memora-desktop-file-drop-icon" aria-hidden="true">+</div>
            <strong>Drop to import into Memoraful</strong>
            <span>PDF, PPTX, Word, images, JSON, or TXT</span>
        </div>`;
    document.body.appendChild(overlay);
    memoraDesktopFileDropOverlay = overlay;
    return overlay;
}

function setMemoraDesktopFileDropOverlayVisible(visible) {
    const overlay = ensureMemoraDesktopFileDropOverlay();
    if (!overlay) return;
    const active = !!visible && isMemoraDesktopFileDropEligible();
    overlay.setAttribute('aria-hidden', active ? 'false' : 'true');
    document.body?.classList.toggle('memora-desktop-file-drop-active', active);
}

function resetMemoraDesktopFileDropState() {
    memoraDesktopFileDropDragDepth = 0;
    setMemoraDesktopFileDropOverlayVisible(false);
}

function showMemoraDesktopFileDropNotice(message, isError = false) {
    const popup = window.showPopupText || window.showPopup;
    if (typeof popup === 'function') {
        popup(message, null, isError);
        return;
    }
    console.warn(message);
}

async function dispatchMemoraDesktopFileDrop(files) {
    const groups = partitionMemoraDesktopFileDropFiles(files);
    const notices = [];

    if (groups.study.length > 0) {
        const importHandler = window.handleImportFiles;
        if (typeof importHandler !== 'function') {
            notices.push('The import system is still loading. Please try the drop again.');
        } else {
            try {
                await importHandler(groups.study, {
                    pdfMode: 'automatic',
                    skipPdfRoutePrompt: true,
                    skipAutoPdfOpen: true,
                    sourceContext: 'desktop-drop'
                });
            } catch (error) {
                console.error('Desktop file drop import failed:', error);
                notices.push(`Import failed: ${error?.message || 'Unknown error'}`);
            }
        }
    }

    if (groups.calendar.length > 0) {
        try {
            await window.MemoraRuntime?.ensureFeature?.('calendar');
            const openCalendarImport = window.openCalendarImportPanel;
            const stageCalendarFile = window.calendarStageIcsFile;
            if (typeof openCalendarImport !== 'function' || typeof stageCalendarFile !== 'function') {
                throw new Error('Calendar import is still loading');
            }
            openCalendarImport();
            for (const file of groups.calendar) {
                await stageCalendarFile(file);
            }
        } catch (error) {
            console.error('Desktop calendar file drop failed:', error);
            notices.push(`Calendar import failed: ${error?.message || 'Unknown error'}`);
        }
    }

    if (groups.image.length > 0) {
        notices.push('This image type cannot be read. Use PNG, JPG, WebP, GIF, or BMP.');
    }
    if (groups.legacyPresentation.length > 0) {
        notices.push('Legacy .ppt files cannot be opened in the browser. Save them as .pptx or PDF, then import again.');
    }
    if (groups.unsupported.length > 0) {
        notices.push('Memoraful accepts PDF, PPTX, Word (.docx), image, JSON, TXT, or ICS files here.');
    }

    if (notices.length > 0) showMemoraDesktopFileDropNotice(notices.join('\n\n'), true);
}

function handleMemoraDesktopFileDrop(event) {
    if (!isMemoraDesktopFileDropEligible() || !hasMemoraDesktopFileTransfer(event)) return;

    const files = getMemoraDesktopDroppedFiles(event);
    if (files.length === 0) return;

    if (isMemoraDesktopFileDropSpecialTarget(event.target)) {
        resetMemoraDesktopFileDropState();
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    resetMemoraDesktopFileDropState();
    void dispatchMemoraDesktopFileDrop(files);
}

// A link dragged onto the workspace would otherwise make the browser navigate away
// from the app. Link drags are claimed only after every inner handler had its turn
// (bubble phase, not already handled), and never over an editable field or a
// specialized drop zone, where dropping text or a link is intentional.
function isMemoraNavigationCapableDrag(event) {
    try {
        const types = Array.from(event?.dataTransfer?.types || []);
        if (types.includes('Files')) return false;
        return types.includes('text/uri-list') || types.includes('text/x-moz-url');
    } catch (_) {
        return false;
    }
}

function isMemoraEditableDropTarget(target) {
    const element = typeof target?.closest === 'function' ? target : target?.parentElement;
    if (!element) return false;
    if (element.closest('input, textarea, select')) return true;
    return !!element.closest('[contenteditable]:not([contenteditable="false"])');
}

function suppressMemoraNavigationDrop(event) {
    if (event.defaultPrevented || !isMemoraNavigationCapableDrag(event)) return;
    if (isMemoraEditableDropTarget(event.target) || isMemoraDesktopFileDropSpecialTarget(event.target)) return;
    event.preventDefault();
    if (event.type === 'dragover' && event.dataTransfer) {
        try { event.dataTransfer.dropEffect = 'none'; } catch (_) { }
    }
}

function bindMemoraDesktopFileDrop() {
    if (memoraDesktopFileDropBound || !isMemoraDesktopFileDropEligible()) return;
    memoraDesktopFileDropBound = true;

    window.addEventListener('dragover', suppressMemoraNavigationDrop);
    window.addEventListener('drop', suppressMemoraNavigationDrop);

    window.addEventListener('dragenter', (event) => {
        if (!hasMemoraDesktopFileTransfer(event)) return;
        event.preventDefault();
        if (isMemoraDesktopFileDropSpecialTarget(event.target)) {
            setMemoraDesktopFileDropOverlayVisible(false);
            return;
        }
        memoraDesktopFileDropDragDepth += 1;
        setMemoraDesktopFileDropOverlayVisible(true);
    }, true);

    window.addEventListener('dragover', (event) => {
        if (!hasMemoraDesktopFileTransfer(event)) return;
        event.preventDefault();
        if (isMemoraDesktopFileDropSpecialTarget(event.target)) {
            setMemoraDesktopFileDropOverlayVisible(false);
            return;
        }
        memoraDesktopFileDropDragDepth = Math.max(1, memoraDesktopFileDropDragDepth);
        setMemoraDesktopFileDropOverlayVisible(true);
    }, true);

    window.addEventListener('dragleave', (event) => {
        if (!hasMemoraDesktopFileTransfer(event)) return;
        if (event.relatedTarget && event.target?.contains?.(event.relatedTarget)) return;
        memoraDesktopFileDropDragDepth = Math.max(0, memoraDesktopFileDropDragDepth - 1);
        if (memoraDesktopFileDropDragDepth === 0) setMemoraDesktopFileDropOverlayVisible(false);
    }, true);

    window.addEventListener('drop', handleMemoraDesktopFileDrop, true);
    window.addEventListener('blur', resetMemoraDesktopFileDropState);
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) resetMemoraDesktopFileDropState();
    });
}

const MEMORA_LIVE_LEFT_RAIL_BOOT_CLASS = 'memora-live-left-rail-booting';
const MEMORA_LIVE_LEFT_RAIL_BOOT_STYLE_ID = 'memora-live-left-rail-boot-style';
const MEMORA_LIVE_LEFT_RAIL_BOOT_TIMEOUT_MS = 6000;

function memoraLiveLeftRailDisabledByQuery() {
    try {
        const value = String(new URLSearchParams(window.location?.search || '').get('leftRail') || '').trim().toLowerCase();
        return value === '0' || value === 'false' || value === 'off';
    } catch (_error) {
        return false;
    }
}

function revealMemoraLiveLeftRail() {
    document.documentElement?.classList.remove(MEMORA_LIVE_LEFT_RAIL_BOOT_CLASS);
}

/* The rail is assembled by seven chained script loads and three injected
   stylesheets. Each hop is a separate round trip, so the native controls bar
   used to paint unstyled — light icon chips on a dark shell — and then jump
   through two more layouts as the plan and polish sheets landed. Cloak the bar
   until the last layer is applied, and warm every asset up front so the chain
   resolves from cache instead of the network. */
function cloakMemoraLiveLeftRailUntilReady() {
    if (document.getElementById(MEMORA_LIVE_LEFT_RAIL_BOOT_STYLE_ID)) return;
    document.documentElement?.classList.add(MEMORA_LIVE_LEFT_RAIL_BOOT_CLASS);
    const style = document.createElement('style');
    style.id = MEMORA_LIVE_LEFT_RAIL_BOOT_STYLE_ID;
    style.textContent = `html.${MEMORA_LIVE_LEFT_RAIL_BOOT_CLASS} #controls-bar{visibility:hidden!important}`;
    document.head?.appendChild(style);

    const started = Date.now();
    const watch = () => {
        const railReady = (document.getElementById('memora-live-rail-nav')?.childElementCount || 0) > 0;
        const polishApplied = !!document.querySelector('link[data-memora-live-left-rail-polish-style]')?.sheet;
        // Never strand the user without navigation if a layer fails to load.
        if ((railReady && polishApplied) || Date.now() - started > MEMORA_LIVE_LEFT_RAIL_BOOT_TIMEOUT_MS) {
            revealMemoraLiveLeftRail();
            return;
        }
        window.requestAnimationFrame(watch);
    };
    window.requestAnimationFrame(watch);
}

function warmMemoraLiveLeftRailAssets(stamp) {
    // Hrefs must match the layers' own injected URLs exactly or the warm-up is wasted.
    const sheets = [
        { id: 'memora-live-left-rail-preview-style', href: `css/live-left-rail-preview.css?v=${encodeURIComponent(`${stamp}-rail5`)}` },
        { id: 'memora-live-left-rail-plan-style', href: `css/live-left-rail-plan.css?v=${encodeURIComponent(`${stamp}-rail-plan1`)}` }
    ];
    sheets.forEach(({ id, href }) => {
        if (document.getElementById(id)) return;
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = href;
        document.head?.appendChild(link);
    });
    if (!document.querySelector('link[data-memora-live-left-rail-polish-style]')) {
        const polish = document.createElement('link');
        polish.rel = 'stylesheet';
        polish.href = `css/live-left-rail-polish.css?v=${encodeURIComponent(`${stamp}-rail-polish4`)}`;
        polish.dataset.memoraLiveLeftRailPolishStyle = 'true';
        document.head?.appendChild(polish);
    }
    [
        `js/modules/app.live-left-rail-host.js?v=${encodeURIComponent(`${stamp}-rail-host14`)}`,
        `js/modules/app.live-left-rail-plan.js?v=${encodeURIComponent(`${stamp}-rail-plan1`)}`,
        `js/modules/app.live-left-rail-routing-v2.js?v=${encodeURIComponent(`${stamp}-rail-routing-v2-2`)}`,
        `js/modules/app.live-left-rail-polish.js?v=${encodeURIComponent(`${stamp}-rail-polish4`)}`,
        `js/modules/app.live-left-rail-context-v3.js?v=${encodeURIComponent(`${stamp}-rail-context-v3-3`)}`,
        // The host boots Final Guard V7; preloading the retired V6 file only drew an unused-preload warning.
        `js/modules/app.live-left-rail-final-guard-v7.js?v=${encodeURIComponent(`${stamp}-rail-final-guard7`)}`,
        `js/modules/app.live-left-rail-tooltip-guard.js?v=${encodeURIComponent(`${stamp}-rail-tooltip2`)}`
    ].forEach((href, index) => {
        const marker = `memora-live-left-rail-preload-${index}`;
        if (document.getElementById(marker)) return;
        const preload = document.createElement('link');
        preload.id = marker;
        preload.rel = 'preload';
        preload.as = 'script';
        preload.href = href;
        document.head?.appendChild(preload);
    });
}

/* Branch-only left-rail preview bootstrap. The live file-drop behavior above is
   intentionally unchanged and the preview can be bypassed with ?leftRail=0. */
function loadMemoraLiveLeftRailPreview() {
    if (document.documentElement?.classList.contains('phone-device')) return;
    if (document.querySelector('script[data-memora-live-left-rail-preview]')) return;
    if (memoraLiveLeftRailDisabledByQuery()) return;
    const stamp = String(document.querySelector('meta[name="memora-build"]')?.content || 'left-rail-preview').trim();
    cloakMemoraLiveLeftRailUntilReady();
    warmMemoraLiveLeftRailAssets(stamp);

    const preview = document.createElement('script');
    preview.src = `js/modules/app.live-left-rail-preview.js?v=${encodeURIComponent(`${stamp}-rail5`)}`;
    preview.async = false;
    preview.dataset.memoraLiveLeftRailPreview = 'true';
    preview.addEventListener('load', () => {
        if (document.querySelector('script[data-memora-live-left-rail-host]')) return;
        const host = document.createElement('script');
        host.src = `js/modules/app.live-left-rail-host.js?v=${encodeURIComponent(`${stamp}-rail-host14`)}`;
        host.async = false;
        host.dataset.memoraLiveLeftRailHost = 'true';
        document.head?.appendChild(host);
    }, { once: true });
    document.head?.appendChild(preview);
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    loadMemoraLiveLeftRailPreview();
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bindMemoraDesktopFileDrop, { once: true });
    } else {
        bindMemoraDesktopFileDrop();
    }
}